import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Truck, Receipt, Package as PackageIcon, TrendingUp, MapPin } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return "—"; } };

export default function MaterialDetailSheet({ open, onOpenChange, materialType, materialId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !materialId || !materialType) return;
    setLoading(true);
    api.get(`/traceability/${materialType}/${materialId}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, materialType, materialId]);

  const material = data?.material;
  const purchases = data?.purchases || [];
  const agg = data?.aggregates;
  const suppliers = data?.suppliers || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-slate-950 border-l border-slate-800 text-slate-100 w-full sm:max-w-2xl overflow-y-auto p-0">
        <SheetHeader className="px-6 py-5 border-b border-slate-800 bg-slate-900/60 sticky top-0 z-10 backdrop-blur">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            İzlenebilirlik · {materialType === "fabric" ? "Kumaş" : "Aksesuar"}
          </div>
          <SheetTitle className="text-slate-50 tracking-tight text-xl">
            {material ? material.name : "Yükleniyor…"}
          </SheetTitle>
          {material && (
            <div className="text-xs font-mono text-blue-500">{material.code}</div>
          )}
        </SheetHeader>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : material ? (
            <>
              {/* Stock / material info */}
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Mevcut Stok</div>
                    {materialType === "fabric" ? (
                      <div className="text-slate-100 font-mono text-lg mt-1">
                        {fmt(material.stock_kg)} <span className="text-slate-500 text-xs">kg</span> ·{" "}
                        {fmt(material.stock_metre)} <span className="text-slate-500 text-xs">m</span>
                      </div>
                    ) : (
                      <div className="text-slate-100 font-mono text-lg mt-1">
                        {fmt(material.stock)} <span className="text-slate-500 text-xs">{material.unit}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Konum</div>
                    <div className="text-slate-100 font-mono mt-1 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-slate-500" />
                      {material.location || "—"}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birincil Tedarikçi</div>
                    <div className="text-slate-100 mt-1 flex items-center gap-1">
                      <Truck className="w-3.5 h-3.5 text-blue-500" />
                      {material.primary_supplier_name || "Atanmamış"}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Aggregates */}
              {agg && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-slate-900 border-slate-800 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">
                      <Receipt className="w-3 h-3" /> Alım
                    </div>
                    <div className="text-slate-100 font-mono text-xl mt-1 tabular-nums">{agg.purchase_count}</div>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">
                      <PackageIcon className="w-3 h-3" /> Toplam
                    </div>
                    <div className="text-slate-100 font-mono text-xl mt-1 tabular-nums">{fmt(agg.total_purchased)}</div>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> Ort. Birim
                    </div>
                    <div className="text-blue-500 font-mono text-xl mt-1 tabular-nums">₺{fmt(agg.avg_unit_price)}</div>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">
                      <Truck className="w-3 h-3" /> Tedarikçi
                    </div>
                    <div className="text-slate-100 font-mono text-xl mt-1 tabular-nums">{agg.supplier_count}</div>
                  </Card>
                </div>
              )}

              {/* Suppliers breakdown */}
              {suppliers.length > 0 && (
                <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-400 font-mono bg-slate-900/60">
                    Tedarikçi Dağılımı
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-slate-800">
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Miktar</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Harcama ₺</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suppliers.map((s) => (
                        <TableRow key={s.supplier_id} className="border-slate-800 hover:bg-slate-800/40">
                          <TableCell className="text-slate-100 text-sm">{s.supplier_name}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-slate-300 text-xs">{s.purchase_count}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-slate-300 text-xs">{fmt(s.total_quantity)}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-blue-500 font-semibold text-xs">₺{fmt(s.total_cost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}

              {/* Purchase history */}
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-400 font-mono bg-slate-900/60">
                  Satın Alma Geçmişi ({purchases.length})
                </div>
                {purchases.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Bu malzeme için alım kaydı yok.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-slate-800">
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım No</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tarih</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Miktar</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim ₺</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fatura</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purchases.map((p) => (
                        <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/40">
                          <TableCell className="font-mono text-xs text-blue-500 font-semibold">{p.purchase_no}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-400">{fmtDate(p.purchase_date)}</TableCell>
                          <TableCell className="text-slate-100 text-xs">{p.supplier_name}</TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums text-slate-100">{fmt(p.quantity)} <span className="text-slate-500">{p.unit}</span></TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums text-slate-300">₺{fmt(p.unit_price)}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-500">{p.invoice_no || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </>
          ) : (
            <div className="text-center text-sm text-slate-500 py-10">Veri yüklenemedi.</div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
